import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * This is a wrapper for forms which handles the submit and sends it to Clerk,
 * our cloud function for managing form submissions.
 *
 * Use this component as a replacement for the `<form>` tag.
 * Initial state is set with the `defaultValue` property on inputs. Radio
 * buttons are using `defaultChecked`.
 *
 * To make this component easy to use, the state of the form is kept private.
 * The component has an `onChange` listener at the top level.
 * Because of this, all inputs should be "uncontrolled" React components.
 */

const BASE_URL =
  'https://europe-west1-accodeing-saas.cloudfunctions.net/clerk_enqueue'

const READY = 'READY'
const PROCESSING = 'PROCESSING'
const SUCCESS = 'SUCCESS'
const FAILURE = 'FAILURE'

const REPLACE = 'replace'
const ABOVE = 'above'
const BELOW = 'below'

/**
 * Sets initial state for the form. Values are fetched
 * from the input's `defaultValue` property. Radio button uses
 * `defaultChecked`.
 */
const initialState = (children, state = {}) => {
  React.Children.forEach(children, (child) => {
    if (
      (child.type === 'input' || child.type === 'select') &&
      child.props.type !== 'submit'
    ) {
      if (child.props.type == 'radio') {
        if (child.props.defaultChecked) {
          state[child.props.name] = child.props.value
        }
      } else {
        state[child.props.name] = child.props.defaultValue
      }
    }
    if (child?.props?.children) {
      initialState(React.Children.toArray(child.props.children), state)
    }
  })
  return state
}

const Clerk = ({
  children,
  className,
  clerkID,
  Processing,
  Success,
  Failure,
  alignment = REPLACE,
}) => {
  const [status, setStatus] = useState(READY)
  const [state, setState] = useState(
    initialState(React.Children.toArray(children))
  )

  const onChange = (event) => {
    setState({ ...state, [event.target.name]: event.target.value })
  }

  const onSubmit = (event) => {
    event.preventDefault()

    setStatus(PROCESSING)

    postData(state)
      .then((response) => response.json())
      .then((data) => {
        setStatus(SUCCESS)
        console.log('Clerk success:', data)
      })
      .catch((error) => {
        setStatus(FAILURE)
        console.error('Clerk error:', error)
      })
  }

  const postData = () =>
    fetch(`${BASE_URL}/${clerkID}`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(state),
    })

  const StatusComponent = () => {
    if (status === PROCESSING) {
      return <Processing />
    } else if (status === SUCCESS) {
      return <Success />
    } else {
      return <Failure />
    }
  }

  return (
    <form
      className={classNames('heimr-clerk', className)}
      onChange={(event) => onChange(event)}
      onSubmit={(event) => onSubmit(event)}
    >
      {status !== READY && (alignment === ABOVE || alignment === REPLACE) && (
        <StatusComponent />
      )}
      {(status === READY || alignment !== REPLACE) && <>{children}</>}
      {status !== READY && alignment === BELOW && <StatusComponent />}
    </form>
  )
}

Clerk.propTypes = {
  alignment: PropTypes.string,
  children: PropTypes.array.isRequired,
  className: PropTypes.string,
  Processing: PropTypes.elementType.isRequired,
  Success: PropTypes.elementType.isRequired,
  Failure: PropTypes.elementType.isRequired,
}

export default Clerk
